import { RATER_SUGGESTION } from "../lib/copy";
import {
  hasObserverScores,
  type DomainCode,
  type ObserverLabel,
  type ObserverView,
} from "../lib/types";
import { domainOverlay } from "../lib/overlay";
import { RaterInvites } from "./RaterInvites";
import { domainColor } from "../lib/domainColors";

const DOMAIN_ORDER: DomainCode[] = ["STR", "REL", "EXE", "INF"];
const LABEL_TITLES: Record<ObserverLabel, string> = {
  hidden_strength: "Hidden strength",
  confirmed: "Confirmed",
  unexpressed: "Unexpressed",
};

/** Section 05. Invites until two raters finish, then the combined view (invites stay until all three are in). */
export function WhatOthersSee({
  takeId,
  observer,
  resultKey,
  readOnly = false,
  print = false,
  selfGifts,
  selfDomains,
}: {
  takeId: string;
  observer: ObserverView | null | undefined;
  resultKey?: string | null;
  /** Staff view: no rater links (they belong to the taker), just what's been seen. */
  readOnly?: boolean;
  /** The results PDF: read-only, and before anyone has finished it says where the links are. */
  print?: boolean;
  /** The taker's own gift ranks, for the side-by-side (You vs. Others). */
  selfGifts?: { gift: string; rank: number }[];
  /** The taker's own domain counts, for the You / Others overlay. */
  selfDomains?: Partial<Record<DomainCode, number>>;
}) {
  const suggestion = observer?.display.suggestion || RATER_SUGGESTION;
  const scored =
    !!observer &&
    hasObserverScores(observer.record) &&
    observer.record.status !== "none";
  // On paper the links can't be copied or shared: the page they're on is where to send them from.
  if (print && !scored) {
    return (
      <div data-testid="rater-invites-print">
        <p className="text-[17px] leading-snug">{suggestion}</p>
        {observer?.display.statusNote ? <p className="mt-3 text-[15px] text-muted">{observer.display.statusNote}</p> : null}
        <p className="mt-3 text-[15px] text-muted">Your three links to send are on your results page, through the link in your results email.</p>
      </div>
    );
  }
  if (readOnly && !scored) {
    return (
      <p className="text-[15px] text-muted">
        {observer?.display.statusNote ?? "No rater results yet."}
      </p>
    );
  }
  if (
    !observer ||
    !hasObserverScores(observer.record) ||
    observer.record.status === "none"
  ) {
    return (
      <RaterInvites
        takeId={takeId}
        resultKey={resultKey}
        suggestion={suggestion}
        note={observer?.display.statusNote}
      />
    );
  }

  const r = observer.record;
  const d = observer.display;
  const names = d.domainNames;
  const domains = [...DOMAIN_ORDER].sort(
    (a, b) => (r.domain_scores[b] ?? 0) - (r.domain_scores[a] ?? 0),
  );
  const byRank = [...r.gifts].sort((a, b) => a.rank - b.rank);
  const groups = d.labelOrder
    .map((label) => ({
      label,
      gifts: byRank
        .filter((g) => r.labels[g.gift] === label)
        .map((g) => g.gift),
    }))
    .filter((g) => g.gifts.length > 0);

  return (
    <div data-testid="observer-view">
      {d.statusNote ? (
        <p className="text-[15px] text-muted">{d.statusNote}</p>
      ) : null}

      <h3 className="heading mt-5 text-[17px]">How they see you engage</h3>
      {selfDomains ? (
        <DomainOverlay
          names={names}
          rows={domainOverlay(selfDomains, r.domain_scores)}
        />
      ) : (
        <ul
          className="mt-3 flex flex-col gap-3"
          aria-label="Domains, as others see you"
        >
          {domains.map((code) => {
            const primary = code === r.primary_domain;
            const score = r.domain_scores[code] ?? 0;
            return (
              <li key={code}>
                <div className="flex items-baseline justify-between text-[15px]">
                  <span className={primary ? "font-bold" : ""}>
                    {names[code] ?? code}
                  </span>
                  <span className="text-muted tabular-nums">{score}</span>
                </div>
                <div
                  className="mt-1.5 h-2.5 w-full rounded-full bg-soft"
                  aria-hidden="true"
                >
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(100, Math.max(0, score))}%`, backgroundColor: domainColor(code).fill }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {r.domain_match ? (
        d.domainMatch ? (
          <p className="mt-4 text-[17px] leading-snug">{d.domainMatch}</p>
        ) : null
      ) : d.domainMismatch ? (
        <p className="mt-4 text-[17px] leading-snug">{d.domainMismatch}</p>
      ) : null}
      {d.agreement ? (
        <p className="mt-2 text-[15px] text-muted">{d.agreement}</p>
      ) : null}
      {d.confirmedPairing ? (
        <p className="mt-4 text-[17px] leading-snug" data-testid="confirmed-pairing">
          {d.confirmedPairing.line}
        </p>
      ) : null}
      {d.confirmedPattern ? (
        <p className="mt-4 text-[17px] leading-snug" data-testid="confirmed-pattern">
          <strong>{d.confirmedPattern.pattern} pattern.</strong> {d.confirmedPattern.line}
        </p>
      ) : null}

      {groups.length > 0 ? (
        <div className="mt-6 flex flex-col gap-3">
          {groups.map((g) => (
            <div
              key={g.label}
              className="rounded-[16px] border border-line bg-panel p-4 print:break-inside-avoid"
            >
              <p className="eyebrow text-muted">{LABEL_TITLES[g.label]}</p>
              <p className="heading mt-1 text-[17px]">{g.gifts.join(", ")}</p>
              <p className="mt-1 text-[15px] leading-snug text-muted">
                {d.labelCopy[g.label]}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      {selfGifts && selfGifts.length > 0 ? (
        <GiftsSideBySide self={selfGifts} others={r.gifts} />
      ) : null}

      {d.lowFamiliarityNote ? (
        <p className="mt-4 text-[14px] text-muted">{d.lowFamiliarityNote}</p>
      ) : null}

      {!readOnly && r.raters_completed < 3 ? (
        <div className="mt-8">
          <RaterInvites
            takeId={takeId}
            resultKey={resultKey}
            suggestion={null}
          />
        </div>
      ) : null}
    </div>
  );
}

const ordinal = (n: number) => `#${n}`;

/**
 * Every gift in your own order, with where the people who rated you put it. Ranks, not
 * scores: the labels above compare ranks too (top 3 against bottom ranks).
 */
function GiftsSideBySide({
  self,
  others,
}: {
  self: { gift: string; rank: number }[];
  others: { gift: string; rank: number }[];
}) {
  const theirs = new Map(others.map((g) => [g.gift, g.rank]));
  const rows = [...self].sort((a, b) => a.rank - b.rank);
  return (
    <div className="mt-8">
      <h3 className="heading text-[17px]">Your gifts, side by side</h3>
      <p className="mt-1 text-[15px] text-muted">
        Where you ranked each gift, and where the people who rated you ranked
        it.
      </p>
      <table
        className="mt-3 w-full text-[15px]"
        aria-label="Gift ranks: you and others"
      >
        <thead>
          <tr className="text-left text-[12px] text-muted">
            <th scope="col" className="py-1.5 font-semibold">
              Gift
            </th>
            <th scope="col" className="w-16 py-1.5 text-right font-semibold">
              You
            </th>
            <th scope="col" className="w-16 py-1.5 text-right font-semibold">
              Others
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => {
            const other = theirs.get(g.gift);
            const shift = other === undefined ? 0 : g.rank - other;
            return (
              <tr key={g.gift} className="border-t border-line">
                <th scope="row" className="py-2 text-left font-normal">
                  {g.gift}
                </th>
                <td className="py-2 text-right tabular-nums">
                  {ordinal(g.rank)}
                </td>
                <td
                  className={`py-2 text-right tabular-nums ${Math.abs(shift) >= 3 ? "font-semibold text-accent-text" : ""}`}
                >
                  {other === undefined ? "–" : ordinal(other)}
                  {shift >= 3 ? (
                    <span className="sr-only">
                      {" "}
                      (higher than you ranked it)
                    </span>
                  ) : null}
                  {shift <= -3 ? (
                    <span className="sr-only"> (lower than you ranked it)</span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** You and the people who rated you, on the same bars: how much each domain stands out for each. */
function DomainOverlay({
  names,
  rows,
}: {
  names: Partial<Record<DomainCode, string>>;
  rows: ReturnType<typeof domainOverlay>;
}) {
  return (
    <div data-testid="domain-overlay">
      <p className="mt-2 flex gap-4 text-[13px] text-muted" aria-hidden="true">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-4 rounded-full"
            style={{ background: "linear-gradient(90deg, var(--color-sun) 25%, var(--color-coral) 25% 50%, var(--color-leaf) 50% 75%, var(--color-teal) 75%)" }}
          />{" "}
          You
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-full bg-muted/50" />{" "}
          Others
        </span>
      </p>
      <ul
        className="mt-3 flex flex-col gap-3"
        aria-label="Domains: you and how others see you"
      >
        {rows.map((row) => (
          <li
            key={row.code}
            aria-label={`${names[row.code] ?? row.code}: you ${row.you}, others ${row.others} (relative)`}
          >
            <span className="text-[15px]">{names[row.code] ?? row.code}</span>
            <div className="mt-1.5 flex flex-col gap-1" aria-hidden="true">
              <div className="h-2.5 w-full rounded-full bg-soft">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${row.you}%`, backgroundColor: domainColor(row.code).fill }}
                  data-testid="bar-you"
                />
              </div>
              <div className="h-2.5 w-full rounded-full bg-soft">
                <div
                  className="h-full rounded-full bg-muted/50"
                  style={{ width: `${row.others}%` }}
                  data-testid="bar-others"
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
